import _ from 'lodash';

import { createApiReducer, chainReducers, INITIAL_API_STATE } from 'src/api';

import {
  FETCH_ACCOUNT_START,
  FETCH_ACCOUNT_SUCCESS,
  FETCH_ACCOUNT_FAILURE,
  UPDATE_ACCOUNT_START,
  UPDATE_ACCOUNT_SUCCESS,
  UPDATE_ACCOUNT_FAILURE,
  UPDATE_ACCOUNT_FIS_START,
  UPDATE_ACCOUNT_FIS_SUCCESS,
  UPDATE_ACCOUNT_FIS_FAILURE,
  SIGN_CONSENT_START,
  SIGN_CONSENT_SUCCESS,
  SIGN_CONSENT_FAILURE,
  ADD_ACCOUNT_CONSENT_TYPE_START,
  ADD_ACCOUNT_CONSENT_TYPE_SUCCESS,
  ADD_ACCOUNT_CONSENT_TYPE_FAILURE,
} from './actions';
import { transformPerson, reducers as peopleReducers } from './person';
import { transformLocation, reducers as locationReducers } from './location';
import { transformLoan, reducers as loanReducers } from './loan';
import {
  transformObligation,
  reducers as obligationReducers,
} from './obligation';
import { reducers as vendorReducers, getTransformVendor } from './vendor';
import {
  transformDocument,
  transformDocumentType,
  reducers as documentReducer,
} from './document';
import { transformProduct, reducers as productReducer } from './product';
import { transformPayment, reducers as paymentReducer } from './payment';
import { transformTask, reducers as taskReducers } from './tasks';
import { transformWebsite, reducers as websiteReducer } from './website';
import { transformDBA, reducers as dbaReducer } from './dba';
/* eslint-disable camelcase */
/* eslint-disable func-names */
export const transformInvite = response => ({
  businessName: response.invitation_business_name,
  ownerEmail: response.invitation_owner_email,
  ownerFirstName: response.invitation_owner_first_name,
  ownerLastName: response.invitation_owner_last_name,
  ownerMobilePhone: response.invitation_owner_mobile_number,
  bgCheckNeeded: response.invitation_bg_check_needed,
  bankingService: response.invitation_for_banking,
  paymentService: response.invitation_for_payments,
  lendingService: response.invitation_for_lending,
  sendEmail: response.send_email,
  hasBusinessPage: response.invitation_has_business_page,
  hasLocationsPage: response.invitation_has_locations_page,
  hasPeoplePage: response.invitation_has_people_page,
  hasVendorsPage: response.invitation_has_vendors_page,
  hasProductsPage: response.invitation_has_products_page,
  hasPaymentsPage: response.invitation_has_payments_page,
  hasDocumentsPage: response.invitation_has_documents_page,
  hasConsentsPage: response.invitation_has_consents_page,
  hasLoansPage: response.invitation_has_loans_page,
  hasObligationsPage: response.invitation_has_obligations_page,
  ndaNeeded: response.invitation_nda_needed,
  servicesOffered: response.invitation_services_offered,
  // new payment info
  pricingFormat: response.account_pricing_format,
  processingType: response.account_processing_type,
  pinBasedDebitRate: response.account_pin_based_debit_rate,
  chargeBackFee: response.account_chargeback_fee,
  salesTrxFee: response.account_sales_trx_fee,
  monthlyMinimumFee: response.account_monthly_minimum_fee,
  pciMonthlyProgramFee: response.account_pci_monthly_program_fee,
  retrievalFee: response.account_retrieval_fee,
  returnFee: response.account_return_fee,
  batchCloseFee: response.account_batch_close_fee,
  terminalIdFee: response.account_terminal_id_fee,
  monthlyStatementFee: response.account_monthly_statement_fee,
  onlinePortalFee: response.account_online_portal_fee,
  pciNonComplianceFee: response.account_pci_non_compliance_fee,
  achRejectFee: response.account_ach_reject_fee,
});

const transformFI = response => ({
  id: response.financial_institution_account_financial_institution,
  status: response.financial_institution_account_status,
  currentCustomer: response.financial_institution_account_current_customer,
  memberId: response.financial_institution_account_member_id,
  bgCheckNeeded: response.financial_institution_account_bg_check_needed,
  completion: {
    businessProfileCompleted: _.get(
      response,
      'financial_institution_account_completion.business_profile_completed',
      false
    ),
    consentsCompleted: _.get(
      response,
      'financial_institution_account_completion.consents_completed',
      false
    ),
    documentsCompleted: _.get(
      response,
      'financial_institution_account_completion.documents_completed',
      false
    ),
    locationCompleted: _.get(
      response,
      'financial_institution_account_completion.location_completed',
      false
    ),
    peopleCompleted: _.get(
      response,
      'financial_institution_account_completion.people_completed',
      false
    ),
    vendorsCompleted: _.get(
      response,
      'financial_institution_account_completion.vendors_completed',
      false
    ),
  },
  services: _.get(
    response,
    'financial_institution_account_services_channels',
    []
  ).map(s => ({
    id: s.id,
    name: s.service_type,
  })),
});

const transformConsent = response => ({
  id: response.consent_type.id,
  name: response.consent_type.consent_type_type,
  longDescription: response.consent_type.consent_type_long_description,
  shortDescription: response.consent_type.consent_type_short_description,
});

const transformConsentType = response => {
  return {
    id: response.id,
    name: response.consent_type_type,
    longDescription: response.consent_type_long_description,
    shortDescription: response.consent_type_short_description,
    isBackgroundCheck: response.consent_type_is_background_check,
    isSolventBackgroundCheck: response.consent_type_is_solvent_background_check,
  };
};
const transformInitiatedBy = (users, agents) => {
  const userIds = users.map(u => u.user_financial_institution_user_id);
  const filter = agents.filter(a => !userIds.includes(a.agent_id));
  if (!filter?.length) {
    return null;
  }
  return {
    agentRole: filter[0].agent_role,
    name: filter[0].agent_user,
    emailAddress: null,
    id: filter[0].agent_id,
  };
};
const transformAgent = (response, primaryAgents, secondaryAgents) => {
  let agentRole = null;
  if (primaryAgents?.length) {
    if (primaryAgents.includes(response.user_financial_institution_user_id)) {
      agentRole = 'Primary Agent';
    }
  }
  if (secondaryAgents?.length) {
    if (secondaryAgents.includes(response.user_financial_institution_user_id)) {
      agentRole = 'Secondary Agent';
    }
  }
  return {
    name: response.user_name,
    emailAddress: response.user_email_address,
    id: response.user_financial_institution_user_id,
    agentRole,
  };
};
const transformAccount = response => {
  const transformVendor = getTransformVendor();

  const financialInstitutions = _.get(
    response,
    'account_financial_institution_accounts',
    []
  ).map(transformFI);

  const firstFI =
    _.get(response, 'account_financial_institution_accounts', [])[0] || {};
  const primaryAgents = response.account_agents
    .filter(obj => obj.agent_role === 'Primary Agent')
    .map(obj => obj.agent_id);
  const secondaryAgents = response.account_agents
    .filter(obj => obj.agent_role === 'Secondary Agent')
    .map(obj => obj.agent_id);
  return {
    id: response.id,
    businessName: response.account_business_name,
    businessSince: response.account_business_since,
    industries: response.account_industries,
    activities: response.account_activities,
    insuranceGroupNames: response.account_insurance_group_names
      ? response.account_insurance_group_names.map(
          ({ insurance_group_name, id }) => ({
            name: insurance_group_name,
            id,
          })
        )
      : [],
    taxId: response.account_tax_id,
    naics: response.account_naics,
    businessEmail: response.account_business_email,
    customerServiceEmail: response.account_customer_service_email,
    customerServicePhone: response.account_customer_service_phone,
    primaryPhone: response.account_primary_phone,
    otherPhone: response.account_other_phone,
    website: response.account_website,
    websites: _.get(response, 'account_websites', []).map(transformWebsite),
    facebook: response.account_facebook,
    linkedin: response.account_linkedin,
    twitter: response.account_twitter,
    instagram: response.account_instagram,
    briefDescription: response.account_brief_description,

    articlesOfIncorporation: response.account_articles_of_incorporation,
    articlesOfOrganization: response.account_articles_of_organization,
    certificateOfRegistration: response.account_certificate_of_registration,
    certificateOfTrust: response.account_certificate_of_trust,
    createdTime: response.account_created_time,
    fictitiousNameFiling: response.account_fictitious_name_filing,
    partnershipAgreement: response.account_partnership_agreement,

    explanationBusiness: response.account_explanation_business,
    explanationLocations: response.account_explanation_locations,
    explanationPeople: response.account_explanation_people,
    explanationVendors: response.account_explanation_vendors,
    explanationProducts: response.account_explanation_products,
    explanationDocuments: response.account_explanation_documents,
    explanationConsents: response.account_explanation_consents,
    explanationLoans: response.account_explanation_loans,
    explanationObligations: response.account_explanation_obligations,
    varSheetMid: response.account_varsheet_mid,
    varSheetIssuedDate: response.account_varsheet,
    reportingMid: response.account_mid,
    midActiveToggle: response.account_mid_active,
    entityType: response.account_entity_type,
    entityStartDate: response.account_entity_start_date,
    acceptsTips: response.account_accepts_tips,

    incorporationState: response.account_incorporation_state,
    currentOwnership: response.account_current_ownership,

    hasBusinessPage: firstFI.financial_institution_account_has_business_page,
    hasLocationsPage: firstFI.financial_institution_account_has_locations_page,
    hasPeoplePage: firstFI.financial_institution_account_has_people_page,
    hasVendorsPage: firstFI.financial_institution_account_has_vendors_page,
    hasProductsPage: firstFI.financial_institution_account_has_products_page,
    hasPaymentsPage: firstFI.financial_institution_account_has_payments_page,
    hasDocumentsPage: firstFI.financial_institution_account_has_documents_page,
    hasConsentsPage: firstFI.financial_institution_account_has_consents_page,
    hasLoansPage: firstFI.financial_institution_account_has_loans_page,
    enablePosPayments: response.account_pos_payments_enabled || false,
    posEnabledAt: response.account_pos_enabled_at,
    primaryAgents,
    secondaryAgents,
    fiAdmins: response.account_financial_institution_admins.map(function(x) {
      return transformAgent(x, primaryAgents, secondaryAgents);
    }),
    initiatedBy: transformInitiatedBy(
      response.account_financial_institution_admins,
      response.account_agents
    ),
    hasObligationsPage:
      firstFI.financial_institution_account_has_obligations_page,
    financialInstitutions,
    people: _.get(response, 'account_users', []).map(function(x) {
      return transformPerson(x, primaryAgents, secondaryAgents);
    }),
    locations: _.get(response, 'account_locations', []).map(transformLocation),
    loans: _.get(response, 'account_loans', []).map(transformLoan),
    tasks: _.get(response, 'account_tasks', []).map(transformTask),
    obligations: _.get(response, 'account_obligations', []).map(
      transformObligation
    ),
    vendors: _.get(response, 'account_vendors', []).map(v => ({
      ...transformVendor(v.vendor_account_vendor),
      addedAt: v.vendor_account_added_date,
    })),
    documents: _.get(response, 'account_documents', []).map(transformDocument),
    consents: _.get(response, 'account_consents', []).map(transformConsent),
    products: _.get(response, 'account_products', []).map(transformProduct),
    payment: transformPayment(_.get(response, 'account_payment', {})),
    documentTypes: _.get(response, 'account_document_types', []).map(
      transformDocumentType
    ),
    consentTypes: _.get(response, 'account_consent_types', []).map(
      transformConsentType
    ),
    dbas: _.get(response, 'account_dbas', []).map(transformDBA),
    servicesOffered:
      _.get(firstFI, 'financial_institution_account_services_offered') || [],
    servicesChannels:
      _.get(firstFI, 'financial_institution_account_services_channels') || [],
  };
};

const accountGetReducer = createApiReducer(
  [FETCH_ACCOUNT_START, FETCH_ACCOUNT_SUCCESS, FETCH_ACCOUNT_FAILURE],
  transformAccount,
  error => _.get(error, 'payload.response', 'unknown error')
);

const accountUpdateReducer = createApiReducer(
  [UPDATE_ACCOUNT_START, UPDATE_ACCOUNT_SUCCESS, UPDATE_ACCOUNT_FAILURE],
  transformAccount,
  error => transformAccount(_.get(error, 'payload.response', {}))
);

const updateItem = (list, key, value, newItem) => {
  return list.map(item => {
    if (item[key] === value) {
      return newItem;
    }
    return item;
  });
};

const accountFIUpdateReducer = createApiReducer(
  [
    UPDATE_ACCOUNT_FIS_START,
    UPDATE_ACCOUNT_FIS_SUCCESS,
    UPDATE_ACCOUNT_FIS_FAILURE,
  ],
  (payload, state) => {
    const newFI = transformFI(payload);

    return {
      ...state.data,
      financialInstitutions: updateItem(
        state.data.financialInstitutions,
        'id',
        newFI.id,
        newFI
      ),
    };
  },
  error => transformFI(_.get(error, 'payload.response', {}))
);

const signConsentReducer = createApiReducer(
  [SIGN_CONSENT_START, SIGN_CONSENT_SUCCESS, SIGN_CONSENT_FAILURE],
  (payload, state) => {
    const newConsent = transformConsent(payload);

    return {
      ...state.data,
      consents: state.data.consents
        .filter(c => c.id !== newConsent.id)
        .concat([newConsent]),
    };
  },
  error => transformFI(_.get(error, 'payload.response', {}))
);

const addConsentTypeReducer = createApiReducer(
  [
    ADD_ACCOUNT_CONSENT_TYPE_START,
    ADD_ACCOUNT_CONSENT_TYPE_SUCCESS,
    ADD_ACCOUNT_CONSENT_TYPE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      consentTypes: [...state.data.consentTypes, transformConsentType(payload)],
    };
  },
  error => transformConsentType(_.get(error, 'payload.response', {}))
);

export const account = chainReducers(
  INITIAL_API_STATE,
  accountGetReducer,
  accountUpdateReducer,
  accountFIUpdateReducer,
  signConsentReducer,
  addConsentTypeReducer,
  ...locationReducers,
  ...loanReducers,
  ...obligationReducers,
  ...peopleReducers,
  ...vendorReducers,
  ...documentReducer,
  ...productReducer,
  ...paymentReducer,
  ...taskReducers,
  ...websiteReducer,
  ...dbaReducer
);
